import { FormattedMessage, useIntl } from "react-intl";
import { Form, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { MdError } from "react-icons/md";
import { toast } from "react-toastify";
import { FC, useEffect, useState } from "react";

import { useGetIntegrationsQuery } from "../../../api/integrationsAPI";
import { useExportProductsMutation } from "../../../api/productsAPI";
import { TProduct } from "../../../api/api.types";

export type TSelectIntegrationData = {
	integration_id: number;
	override_existing_status: boolean;
};

type TShowIntegrationProps = {
	show: boolean;
	onHide: () => void;
	selectedProducts: number[];
	setSelectedProducts: (value: number[]) => void;
	products: TProduct[];
};

const SelectIntegration: FC<TShowIntegrationProps> = ({
	show,
	onHide,
	selectedProducts,
	setSelectedProducts,
	products,
}) => {
	const [showTikTokVerificationWarning, setShowTikTokVerificationWarning] =
		useState(false);

	const isSomeProductsNotVerifiedForTikTok = products.some(
		(product) =>
			selectedProducts.includes(product.id) &&
			!product.is_available_for_tiktok
	);

	console.log(
		isSomeProductsNotVerifiedForTikTok,
		showTikTokVerificationWarning
	);

	const intl = useIntl();

	const [exportProducts] = useExportProductsMutation();
	const { data: integrations } = useGetIntegrationsQuery();

	const { register, handleSubmit, watch } = useForm<TSelectIntegrationData>();

	const integrationId = watch("integration_id");

	const onSubmit = (data: TSelectIntegrationData) => {
		const object = {
			ids_list: selectedProducts,
			integration_id: data.integration_id,
			override_existing_status: data.override_existing_status,
		};
		exportProducts(object).then((res) => {
			if ("error" in res) {
				const status = (res.error as { status: number }).status;

				if (status !== 403) {
					const errorsData = (
						res.error as {
							data: {
								error?: string[];
								integration_id?: string[];
							};
						}
					)?.data;

					if (errorsData?.error && errorsData.error.length > 0) {
						toast.error(errorsData.error[0]);
					} else if (
						errorsData?.integration_id &&
						errorsData.integration_id.length > 0
					) {
						toast.error(errorsData.integration_id[0]);
					} else {
						toast.error(
							intl.formatMessage({ id: "somethingWentWrong" })
						);
					}
				}
			} else {
				setSelectedProducts([]);
				toast.success(
					intl.formatMessage({ id: "productsSuccessfullyExported" })
				);
				onHide();
			}
		});
	};

	useEffect(() => {
		if (integrationId) {
			const selectedIntegration = integrations?.results.find(
				(integration) => integration.id === Number(integrationId)
			);

			if (selectedIntegration?.partner === "TikTok Integration") {
				setShowTikTokVerificationWarning(true);
			} else {
				setShowTikTokVerificationWarning(false);
			}
		} else {
			setShowTikTokVerificationWarning(false);
		}
	}, [integrationId, integrations]);

	return (
		<Modal className="modal-500" centered show={show} onHide={onHide}>
			<Modal.Header className="modal__header" closeButton>
				<Modal.Title className="modal__title">
					<FormattedMessage id="selectIntegration" />
				</Modal.Title>
			</Modal.Header>
			<Modal.Body className="pt-0">
				<Form onSubmit={handleSubmit(onSubmit)}>
					{isSomeProductsNotVerifiedForTikTok &&
						showTikTokVerificationWarning && (
							<div className="tiktok-verification-warning">
								<MdError
									style={{ minWidth: "30px" }}
									size={30}
									color="#ffcc00"
								/>
								<FormattedMessage id="someOfTheProductsYouSelectedAreNotYetVerifiedForTikTok" />
							</div>
						)}
					<Form.Select
						defaultValue=""
						className="default-input mt-2"
						{...register("integration_id")}
					>
						<option value="">
							<FormattedMessage id="selectIntegration" />
						</option>
						{integrations &&
							integrations.results.map((integration) => {
								if (integration.is_seller === true) {
									return (
										<option
											key={integration.id}
											value={integration.id}
										>
											{integration.name}
										</option>
									);
								} else {
									return null;
								}
							})}
					</Form.Select>
					<Form.Group className="d-flex align-items-center mt-1">
						<Form.Check
							className="me-2"
							{...register("override_existing_status")}
						/>
						<label className="default-input-label">
							<FormattedMessage id="overrideExistingProduct" />
						</label>
					</Form.Group>
					<div className="d-flex justify-content-end">
						<button type="submit" className="ice-button w-100 mt-3">
							<FormattedMessage id="exportProductsToStore" />
						</button>
					</div>
				</Form>
			</Modal.Body>
		</Modal>
	);
};

export default SelectIntegration;
